import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  DocumentArrowDownIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { abastecimentoFinanceiroService, veiculoService, colaboradorService } from '../services/api';
import AbastecimentoFinanceiroForm from '../components/AbastecimentoFinanceiroForm';

const AbastecimentosFinanceiros = () => {
  const [filtros, setFiltros] = useState({
    veiculo: '',
    status: '',
    dataInicio: '',
    dataFim: '',
    tipoCombustivel: '',
    page: 1,
    limit: 10
  });
  const [buscaVeiculo, setBuscaVeiculo] = useState('');
  const [mostrarDropdownVeiculos, setMostrarDropdownVeiculos] = useState(false);
  const [abastecimentoSelecionado, setAbastecimentoSelecionado] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [mostrarDetalhes, setMostrarDetalhes] = useState(false);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  // const [mostrarEstatisticas, setMostrarEstatisticas] = useState(false);
  const [motivoRejeicao, setMotivoRejeicao] = useState('');

  const queryClient = useQueryClient();

  // Buscar abastecimentos
  const { data: abastecimentosData, isLoading, error } = useQuery({
    queryKey: ['abastecimentos-financeiros', filtros],
    queryFn: () => abastecimentoFinanceiroService.listar(filtros),
    keepPreviousData: true,
    retry: 1,
    retryDelay: 1000
  });

  // Buscar veículos para filtros
  const { data: veiculosData } = useQuery({
    queryKey: ['veiculos'],
    queryFn: () => veiculoService.listar({ ativo: true })
  });

  // Buscar colaboradores para filtros
  // const { data: colaboradoresData } = useQuery({
  //   queryKey: ['colaboradores'],
  //   queryFn: () => colaboradorService.listar({ ativo: true })
  // });

  // Buscar estatísticas do veículo selecionado
  const { data: estatisticasData } = useQuery({
    queryKey: ['estatisticas-abastecimento', filtros.veiculo, filtros.dataInicio, filtros.dataFim],
    queryFn: () => abastecimentoFinanceiroService.estatisticas(filtros.veiculo, {
      dataInicio: filtros.dataInicio,
      dataFim: filtros.dataFim
    }),
    enabled: !!filtros.veiculo
  });

  // Mutations
  const aprovarMutation = useMutation({
    mutationFn: abastecimentoFinanceiroService.aprovar,
    onSuccess: () => {
      toast.success('Abastecimento aprovado com sucesso!');
      queryClient.invalidateQueries(['abastecimentos-financeiros']);
      setMostrarDetalhes(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao aprovar abastecimento');
    }
  });

  const conferirMutation = useMutation({
    mutationFn: abastecimentoFinanceiroService.conferir,
    onSuccess: () => {
      toast.success('Abastecimento conferido com sucesso!');
      queryClient.invalidateQueries(['abastecimentos-financeiros']);
      setMostrarDetalhes(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao conferir abastecimento');
    }
  });

  const rejeitarMutation = useMutation({
    mutationFn: ({ id, motivo }) => abastecimentoFinanceiroService.rejeitar(id, motivo),
    onSuccess: () => {
      toast.success('Abastecimento rejeitado com sucesso!');
      queryClient.invalidateQueries(['abastecimentos-financeiros']);
      setMostrarDetalhes(false);
      setMotivoRejeicao('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao rejeitar abastecimento');
    }
  });

  const excluirMutation = useMutation({
    mutationFn: abastecimentoFinanceiroService.excluir,
    onSuccess: () => {
      toast.success('Abastecimento excluído com sucesso!');
      queryClient.invalidateQueries(['abastecimentos-financeiros']);
      setMostrarDetalhes(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir abastecimento');
    }
  });

  // Garantir que os dados sejam sempre arrays válidos
  const abastecimentos = useMemo(() => {
    if (!abastecimentosData) return [];
    if (abastecimentosData.success && Array.isArray(abastecimentosData.data)) {
      return abastecimentosData.data;
    }
    return [];
  }, [abastecimentosData]);

  const veiculos = useMemo(() => {
    if (!veiculosData) return [];
    if (veiculosData.success && Array.isArray(veiculosData.data)) {
      return veiculosData.data;
    }
    return [];
  }, [veiculosData]);

  // Filtrar veículos baseado na busca
  const veiculosFiltrados = useMemo(() => {
    if (!buscaVeiculo.trim()) return veiculos;
    
    return veiculos.filter(veiculo => 
      veiculo.placa.toLowerCase().includes(buscaVeiculo.toLowerCase()) ||
      veiculo.modelo.toLowerCase().includes(buscaVeiculo.toLowerCase()) ||
      veiculo.marca.toLowerCase().includes(buscaVeiculo.toLowerCase())
    );
  }, [veiculos, buscaVeiculo]);

  // const colaboradores = useMemo(() => {
  //   if (!colaboradoresData) return [];
  //   if (colaboradoresData.success && Array.isArray(colaboradoresData.data)) {
  //     return colaboradoresData.data;
  //   }
  //   return [];
  // }, [colaboradoresData]);

  const estatisticas = estatisticasData?.data || {};

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.veiculo-search-container')) {
        setMostrarDropdownVeiculos(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor,
      page: 1 // Reset para primeira página ao filtrar
    }));
  };

  const handleAprovar = (id) => {
    if (window.confirm('Tem certeza que deseja aprovar este abastecimento?')) {
      aprovarMutation.mutate(id);
    }
  };

  const handleConferir = (id) => {
    if (window.confirm('Tem certeza que deseja conferir este abastecimento?')) {
      conferirMutation.mutate(id);
    }
  };

  const handleRejeitar = (id) => {
    if (!motivoRejeicao.trim()) {
      toast.error('Motivo da rejeição é obrigatório');
      return;
    }
    if (window.confirm('Tem certeza que deseja rejeitar este abastecimento?')) {
      rejeitarMutation.mutate({ id, motivo: motivoRejeicao });
    }
  };

  const handleExcluir = (id) => {
    if (window.confirm('Tem certeza que deseja excluir este abastecimento?')) {
      excluirMutation.mutate(id);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pendente: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pendente' },
      aprovado: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Aprovado' },
      conferido: { bg: 'bg-green-100', text: 'text-green-800', label: 'Conferido' },
      rejeitado: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejeitado' }
    };
    
    const config = statusConfig[status] || statusConfig.pendente;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getTipoCombustivelLabel = (tipo) => {
    const tipos = {
      gasolina: 'Gasolina',
      etanol: 'Etanol',
      diesel: 'Diesel',
      gnv: 'GNV',
      eletrico: 'Elétrico'
    };
    return tipos[tipo] || tipo;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Erro ao carregar abastecimentos</h3>
            <div className="mt-2 text-sm text-red-700">
              {error.response?.data?.message || 'Erro interno do servidor'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-purple-900 rounded-2xl p-6 md:p-8 text-white shadow-xl flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-indigo-300 text-sm font-medium uppercase tracking-widest mb-1">Frota</p>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
            Abastecimentos Financeiros
          </h2>
          <p className="mt-1 text-indigo-200 text-sm">
            Controle de abastecimentos e cálculos de custo
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
          <button
            type="button"
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            className="inline-flex items-center px-4 py-2 border border-white/20 rounded-xl text-sm font-medium text-white bg-white/10 hover:bg-white/20 backdrop-blur transition"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filtros
          </button>
          {filtros.veiculo && (
            <button
              type="button"
              onClick={() => {/* setMostrarEstatisticas(true) */}}
              className="inline-flex items-center px-4 py-2 border border-white/20 rounded-xl text-sm font-medium text-white bg-white/10 hover:bg-white/20 backdrop-blur transition"
            >
              <ChartBarIcon className="h-4 w-4 mr-2" />
              Estatísticas
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setAbastecimentoSelecionado(null);
              setMostrarFormulario(true);
            }}
            className="inline-flex items-center px-5 py-2.5 border border-transparent rounded-xl text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-500 hover:opacity-90 shadow-lg shadow-indigo-500/30 transition"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Novo Abastecimento
          </button>
        </div>
      </div>

      {/* Estatísticas */}
      {filtros.veiculo && estatisticas.totalAbastecimentos > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Estatísticas do Veículo</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{estatisticas.totalAbastecimentos}</div>
              <div className="text-sm text-blue-800">Total de Abastecimentos</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{estatisticas.totalLitros?.toFixed(1)}L</div>
              <div className="text-sm text-green-800">Total de Litros</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">R$ {estatisticas.totalValor?.toFixed(2)}</div>
              <div className="text-sm text-purple-800">Valor Total</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">{estatisticas.totalKm?.toFixed(0)} km</div>
              <div className="text-sm text-orange-800">Quilometragem Total</div>
            </div>
            <div className="bg-indigo-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-indigo-600">R$ {estatisticas.custoMedioPorLitro?.toFixed(2)}</div>
              <div className="text-sm text-indigo-800">Custo Médio por Litro</div>
            </div>
            <div className="bg-pink-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-pink-600">R$ {estatisticas.custoMedioPorKm?.toFixed(2)}</div>
              <div className="text-sm text-pink-800">Custo Médio por KM</div>
            </div>
            <div className="bg-teal-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-teal-600">{estatisticas.consumoMedio?.toFixed(1)} L/km</div>
              <div className="text-sm text-teal-800">Consumo Médio</div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      {mostrarFiltros && (
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="veiculo-search-container">
              <label className="block text-sm font-medium text-gray-700">Buscar Veículo</label>
              <div className="relative">
                <input
                  type="text"
                  value={buscaVeiculo}
                  onChange={(e) => {
                    setBuscaVeiculo(e.target.value);
                    setMostrarDropdownVeiculos(true);
                  }}
                  onFocus={() => setMostrarDropdownVeiculos(true)}
                  placeholder="Digite placa, modelo ou marca..."
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm pr-10"
                />
                <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>
              
              {/* Dropdown de veículos filtrados */}
              {mostrarDropdownVeiculos && buscaVeiculo && veiculosFiltrados.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {veiculosFiltrados.map(veiculo => (
                    <button
                      key={veiculo._id}
                      onClick={() => {
                        handleFiltroChange('veiculo', veiculo._id);
                        setBuscaVeiculo(`${veiculo.placa} - ${veiculo.modelo}`);
                        setMostrarDropdownVeiculos(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                    >
                      <div className="font-medium text-gray-900">{veiculo.placa}</div>
                      <div className="text-sm text-gray-500">{veiculo.marca} {veiculo.modelo}</div>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Mostrar veículo selecionado */}
              {filtros.veiculo && (
                <div className="mt-2 flex items-center justify-between bg-purple-50 px-3 py-2 rounded-md">
                  <span className="text-sm text-purple-800">
                    {veiculos.find(v => v._id === filtros.veiculo)?.placa} - {veiculos.find(v => v._id === filtros.veiculo)?.modelo}
                  </span>
                  <button
                    onClick={() => {
                      handleFiltroChange('veiculo', '');
                      setBuscaVeiculo('');
                      setMostrarDropdownVeiculos(false);
                    }}
                    className="text-purple-600 hover:text-purple-800"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                value={filtros.status}
                onChange={(e) => handleFiltroChange('status', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              >
                <option value="">Todos os status</option>
                <option value="pendente">Pendente</option>
                <option value="aprovado">Aprovado</option>
                <option value="conferido">Conferido</option>
                <option value="rejeitado">Rejeitado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo de Combustível</label>
              <select
                value={filtros.tipoCombustivel}
                onChange={(e) => handleFiltroChange('tipoCombustivel', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              >
                <option value="">Todos os tipos</option>
                <option value="gasolina">Gasolina</option>
                <option value="etanol">Etanol</option>
                <option value="diesel">Diesel</option>
                <option value="gnv">GNV</option>
                <option value="eletrico">Elétrico</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Data Início</label>
              <input
                type="date"
                value={filtros.dataInicio}
                onChange={(e) => handleFiltroChange('dataInicio', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Data Fim</label>
              <input
                type="date"
                value={filtros.dataFim}
                onChange={(e) => handleFiltroChange('dataFim', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Lista de Abastecimentos */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-600 mb-2">Erro ao carregar abastecimentos</p>
            <p className="text-gray-500 text-sm">{error.message}</p>
            <p className="text-gray-500 text-xs mt-2">Debug: {JSON.stringify(error)}</p>
          </div>
        ) : !Array.isArray(abastecimentos) ? (
          <div className="text-center py-8">
            <p className="text-red-600 mb-2">Erro: Dados inválidos</p>
            <p className="text-gray-500 text-sm">Os dados recebidos não são um array válido</p>
            <p className="text-gray-500 text-xs mt-2">Tipo recebido: {typeof abastecimentos}</p>
          </div>
        ) : abastecimentos.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum abastecimento encontrado</h3>
            <p className="mt-1 text-sm text-gray-500">
              Comece criando um novo abastecimento.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {abastecimentos.map((abastecimento) => (
              <li key={abastecimento._id}>
                <div className="px-4 py-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-purple-600 font-medium text-sm">
                          {abastecimento.veiculo?.placa?.substring(0, 2) || 'AB'}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">
                          {abastecimento.veiculo?.placa} - {abastecimento.veiculo?.modelo}
                        </p>
                        {getStatusBadge(abastecimento.status)}
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                        <p>
                          {new Date(abastecimento.data).toLocaleDateString('pt-BR')} - 
                          {getTipoCombustivelLabel(abastecimento.tipoCombustivel)} - 
                          {abastecimento.quantidadeLitros}L - 
                          R$ {abastecimento.valorTotal?.toFixed(2)}
                        </p>
                        <p>
                          NF-e: {abastecimento.nfe?.numero} - 
                          Hodômetro: {abastecimento.hodometro?.toLocaleString()} km - 
                          Responsável: {abastecimento.responsavel?.nome}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setAbastecimentoSelecionado(abastecimento);
                        setMostrarDetalhes(true);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                      title="Ver detalhes"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    {abastecimento.status === 'pendente' && (
                      <>
                        <button
                          onClick={() => {
                            setAbastecimentoSelecionado(abastecimento);
                            setMostrarFormulario(true);
                          }}
                          className="text-gray-400 hover:text-gray-600"
                          title="Editar"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleAprovar(abastecimento._id)}
                          className="text-blue-400 hover:text-blue-600"
                          title="Aprovar"
                          disabled={aprovarMutation.isLoading}
                        >
                          <CheckIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            setAbastecimentoSelecionado(abastecimento);
                            setMotivoRejeicao('');
                          }}
                          className="text-red-400 hover:text-red-600"
                          title="Rejeitar"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </>
                    )}
                    {abastecimento.status === 'aprovado' && (
                      <button
                        onClick={() => handleConferir(abastecimento._id)}
                        className="text-green-400 hover:text-green-600"
                        title="Conferir"
                        disabled={conferirMutation.isLoading}
                      >
                        <CheckIcon className="h-5 w-5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleExcluir(abastecimento._id)}
                      className="text-red-400 hover:text-red-600"
                      title="Excluir"
                      disabled={excluirMutation.isLoading}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Paginação */}
      {abastecimentosData?.pagination && abastecimentosData.pagination.pages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handleFiltroChange('page', Math.max(1, filtros.page - 1))}
              disabled={filtros.page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              onClick={() => handleFiltroChange('page', Math.min(abastecimentosData.pagination.pages, filtros.page + 1))}
              disabled={filtros.page === abastecimentosData.pagination.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próximo
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Mostrando{' '}
                <span className="font-medium">
                  {((filtros.page - 1) * filtros.limit) + 1}
                </span>{' '}
                até{' '}
                <span className="font-medium">
                  {Math.min(filtros.page * filtros.limit, abastecimentosData.pagination.total)}
                </span>{' '}
                de{' '}
                <span className="font-medium">{abastecimentosData.pagination.total}</span>{' '}
                resultados
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => handleFiltroChange('page', Math.max(1, filtros.page - 1))}
                  disabled={filtros.page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => handleFiltroChange('page', Math.min(abastecimentosData.pagination.pages, filtros.page + 1))}
                  disabled={filtros.page === abastecimentosData.pagination.pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próximo
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes */}
      {mostrarDetalhes && abastecimentoSelecionado && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setMostrarDetalhes(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Detalhes do Abastecimento
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Informações do Veículo */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">Veículo</h4>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Placa</label>
                            <p className="mt-1 text-sm text-gray-900">{abastecimentoSelecionado.veiculo?.placa || '-'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Modelo</label>
                            <p className="mt-1 text-sm text-gray-900">{abastecimentoSelecionado.veiculo?.modelo || '-'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Tipo</label>
                            <p className="mt-1 text-sm text-gray-900">{abastecimentoSelecionado.veiculo?.tipo || '-'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Informações do Abastecimento */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">Abastecimento</h4>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Data</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {new Date(abastecimentoSelecionado.data).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Tipo de Combustível</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {getTipoCombustivelLabel(abastecimentoSelecionado.tipoCombustivel)}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Quantidade</label>
                            <p className="mt-1 text-sm text-gray-900">{abastecimentoSelecionado.quantidadeLitros}L</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Valor por Litro</label>
                            <p className="mt-1 text-sm text-gray-900">R$ {abastecimentoSelecionado.valorLitro?.toFixed(2)}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Valor Total</label>
                            <p className="mt-1 text-sm text-gray-900 font-medium">R$ {abastecimentoSelecionado.valorTotal?.toFixed(2)}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hodômetro</label>
                            <p className="mt-1 text-sm text-gray-900">{abastecimentoSelecionado.hodometro?.toLocaleString()} km</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">KM Percorridos</label>
                            <p className="mt-1 text-sm text-gray-900">{abastecimentoSelecionado.quilometragemPercorrida?.toLocaleString()} km</p>
                          </div>
                        </div>
                      </div>

                      {/* Informações da NF-e */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">Nota Fiscal</h4>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Número</label>
                            <p className="mt-1 text-sm text-gray-900">{abastecimentoSelecionado.nfe?.numero || '-'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Série</label>
                            <p className="mt-1 text-sm text-gray-900">{abastecimentoSelecionado.nfe?.serie || '-'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Data de Emissão</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {abastecimentoSelecionado.nfe?.dataEmissao ? 
                                new Date(abastecimentoSelecionado.nfe.dataEmissao).toLocaleDateString('pt-BR') : '-'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Valor da NF-e</label>
                            <p className="mt-1 text-sm text-gray-900">R$ {abastecimentoSelecionado.nfe?.valorNfe?.toFixed(2)}</p>
                          </div>
                          {abastecimentoSelecionado.nfe?.chaveAcesso && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Chave de Acesso</label>
                              <p className="mt-1 text-sm text-gray-900 font-mono text-xs">
                                {abastecimentoSelecionado.nfe.chaveAcesso}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Informações do Posto */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">Posto</h4>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Nome</label>
                            <p className="mt-1 text-sm text-gray-900">{abastecimentoSelecionado.posto?.nome || '-'}</p>
                          </div>
                          {abastecimentoSelecionado.posto?.cnpj && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700">CNPJ</label>
                              <p className="mt-1 text-sm text-gray-900">{abastecimentoSelecionado.posto.cnpj}</p>
                            </div>
                          )}
                          {abastecimentoSelecionado.posto?.endereco && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Endereço</label>
                              <p className="mt-1 text-sm text-gray-900">
                                {abastecimentoSelecionado.posto.endereco.rua && abastecimentoSelecionado.posto.endereco.numero && 
                                  `${abastecimentoSelecionado.posto.endereco.rua}, ${abastecimentoSelecionado.posto.endereco.numero}`}
                                {abastecimentoSelecionado.posto.endereco.bairro && ` - ${abastecimentoSelecionado.posto.endereco.bairro}`}
                                {abastecimentoSelecionado.posto.endereco.cidade && ` - ${abastecimentoSelecionado.posto.endereco.cidade}`}
                                {abastecimentoSelecionado.posto.endereco.estado && `/${abastecimentoSelecionado.posto.endereco.estado}`}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Informações do Responsável */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">Responsável</h4>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Nome</label>
                            <p className="mt-1 text-sm text-gray-900">{abastecimentoSelecionado.responsavel?.nome || '-'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Matrícula</label>
                            <p className="mt-1 text-sm text-gray-900">{abastecimentoSelecionado.responsavel?.matricula || '-'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Status</label>
                            <div className="mt-1">
                              {getStatusBadge(abastecimentoSelecionado.status)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Custos Calculados */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">Custos Calculados</h4>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Custo por Litro</label>
                            <p className="mt-1 text-sm text-gray-900">R$ {abastecimentoSelecionado.custoPorLitro?.toFixed(2)}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Custo por KM</label>
                            <p className="mt-1 text-sm text-gray-900">R$ {abastecimentoSelecionado.custoPorKm?.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Observações */}
                    {abastecimentoSelecionado.observacoes && (
                      <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">Observações</h4>
                        <p className="text-sm text-gray-900">{abastecimentoSelecionado.observacoes}</p>
                      </div>
                    )}

                    {/* NF-e */}
                    {abastecimentoSelecionado.nfe?.arquivo && (
                      <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">Arquivo da NF-e</h4>
                        <button
                          onClick={() => abastecimentoFinanceiroService.baixarNfe(abastecimentoSelecionado.nfe.arquivo)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                        >
                          <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                          Baixar NF-e
                        </button>
                      </div>
                    )}

                    {/* Motivo da Rejeição */}
                    {abastecimentoSelecionado.status === 'rejeitado' && abastecimentoSelecionado.motivoRejeicao && (
                      <div className="mt-6 bg-red-50 p-4 rounded-lg">
                        <h4 className="text-lg font-medium text-red-900 mb-3">Motivo da Rejeição</h4>
                        <p className="text-sm text-red-700">{abastecimentoSelecionado.motivoRejeicao}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setMostrarDetalhes(false)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Rejeição */}
      {abastecimentoSelecionado && !mostrarDetalhes && !mostrarFormulario && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setAbastecimentoSelecionado(null)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Rejeitar Abastecimento
                    </h3>
                    <div className="mb-4">
                      <p className="text-sm text-gray-500">
                        Veículo: {abastecimentoSelecionado.veiculo?.placa} - {abastecimentoSelecionado.veiculo?.modelo}
                      </p>
                      <p className="text-sm text-gray-500">
                        Data: {new Date(abastecimentoSelecionado.data).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Motivo da Rejeição *
                      </label>
                      <textarea
                        value={motivoRejeicao}
                        onChange={(e) => setMotivoRejeicao(e.target.value)}
                        rows={3}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                        placeholder="Digite o motivo da rejeição..."
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => handleRejeitar(abastecimentoSelecionado._id)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  disabled={rejeitarMutation.isLoading || !motivoRejeicao.trim()}
                >
                  {rejeitarMutation.isLoading ? 'Rejeitando...' : 'Rejeitar'}
                </button>
                <button
                  type="button"
                  onClick={() => setAbastecimentoSelecionado(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Formulário */}
      {mostrarFormulario && (
        <AbastecimentoFinanceiroForm
          abastecimento={abastecimentoSelecionado}
          onClose={() => {
            setMostrarFormulario(false);
            setAbastecimentoSelecionado(null);
          }}
          onSuccess={() => {
            setMostrarFormulario(false);
            setAbastecimentoSelecionado(null);
            queryClient.invalidateQueries(['abastecimentos-financeiros']);
          }}
        />
      )}
    </div>
  );
};

export default AbastecimentosFinanceiros;
